import React, { useCallback, useEffect, useState } from 'react';
import CircleCloseIcon from 'ui/Icons/circleClose.component.svg';
import MagnifyingIcon from 'ui/Icons/magnifying-glass.component.svg';
import Loading from 'ui/AsyncSearchDropdown/Loading';
import TextInput from 'pureUi/TextInput';
import classNames from 'classnames';
import { useBasketReferenceSearch } from './useBasketQueries';

const DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS_DISPLAY = 9;
const SUGGESTIONS_MIN_LENGTH = 3;

export interface BasketReferenceSearchProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (humanReadableId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputClassName?: string;
  id?: string;
}

export const BasketReferenceSearch: React.FC<BasketReferenceSearchProps> = ({
  value,
  onChange,
  onSelect,
  disabled = false,
  placeholder = 'BK-999AF-24-00001',
  inputClassName,
  id = 'basket-reference-id',
}) => {
  const [selectedReference, setSelectedReference] = useState<string | null>(null);
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [value]);

  // Clear selection when value changes away from the selected reference
  useEffect(() => {
    if (selectedReference && value.trim() !== selectedReference) {
      setSelectedReference(null);
    }
  }, [value, selectedReference]);

  const trimmedDebounced = debouncedValue.trim();
  const isSelected = !!selectedReference && trimmedDebounced === selectedReference;
  const searchEnabled = trimmedDebounced.length >= SUGGESTIONS_MIN_LENGTH && !isSelected;

  const { data: suggestions = [], isFetching } = useBasketReferenceSearch(trimmedDebounced, searchEnabled);

  const handleSelect = useCallback(
    (humanReadableId: string) => {
      setSelectedReference(humanReadableId);
      onSelect(humanReadableId);
      onChange(humanReadableId);
    },
    [onSelect, onChange]
  );

  const handleClear = useCallback(() => {
    setSelectedReference(null);
    onChange('');
    onSelect('');
  }, [onChange, onSelect]);

  const showDropdown = searchEnabled && value.trim().length >= SUGGESTIONS_MIN_LENGTH;
  const displaySuggestions = suggestions.slice(0, MAX_SUGGESTIONS_DISPLAY);

  return (
    <div className="flex flex-col relative w-full">
      {/* @ts-ignore - pureUi TextInput children */}
      <TextInput
        id={id}
        onChange={e => onChange(e.currentTarget.value)}
        placeholder={placeholder}
        className="flex flex-col relative"
        value={value}
        inputClassName={classNames(
          '!py-[10px] !px-[30px] !text-black !bg-ivory border border-solid border-gray-40',
          inputClassName
        )}
        disabled={disabled}
      >
        <CircleCloseIcon
          onClick={() => !disabled && handleClear()}
          className={classNames(
            'block cursor-pointer fill-brown-80 hover:fill-brown-120 absolute w-[18px] top-[10px] right-[10px]',
            { hidden: value === '' || disabled }
          )}
        />
        <MagnifyingIcon className="absolute w-[18px] top-[10px] left-[10px] fill-light-gray rotate-90" />
      </TextInput>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-full z-[100] mt-0 flex flex-col max-h-[295px] overflow-auto border border-gray-20 border-t-0 bg-white shadow"
          role="listbox"
          aria-label="Basket reference suggestions"
        >
          {isFetching ? (
            <div className="py-2">
              <Loading />
            </div>
          ) : displaySuggestions.length > 0 ? (
            <>
              <div className="text-gray-100 text-[13px] mt-[10px] px-[10px] font-normal font-pt-sans leading-[15px] tracking-wide">
                BASKET REFERENCES (
                {suggestions.length > MAX_SUGGESTIONS_DISPLAY
                  ? `${MAX_SUGGESTIONS_DISPLAY}+`
                  : suggestions.length}
                )
              </div>
              <div className="search-options">
                {displaySuggestions.map((humanReadableId, index, arr) => (
                  <React.Fragment key={humanReadableId}>
                    <div
                      role="option"
                      tabIndex={0}
                      aria-selected={false}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelect(humanReadableId);
                        }
                      }}
                      onClick={() => handleSelect(humanReadableId)}
                      className="p-[5px] my-[5px] mx-[10px] flex flex-col justify-center font-pt-sans rounded-[3px] hover:bg-gray-10 cursor-pointer"
                    >
                      <span className="h-[19px]">{humanReadableId}</span>
                    </div>
                    {index + 1 < arr.length && (
                      <div className="h-[0px] border border-gray-10 mx-[10px]" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </>
          ) : (
            <div className="text-black text-[15px] font-normal mt-[10px] px-[10px] pb-2 italic font-pt-sans">
              No results found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
